import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftOutlined, DeleteOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Skeleton, Form, Space, Spin, Tag } from "antd";
import { colornames } from "color-name-list";
import PropTypes from "prop-types";
import { BsShieldFillCheck, BsShieldFillExclamation } from "react-icons/bs";
import { useHistory, useLocation } from "react-router-dom";
import apiClient from "../../helpers/apiClient";
import useUrlParamState from "../../hooks/useUrlParamState";
import BlockCar from "../reservations/components/subcompdash/BlockCar";
import CarDocumentsSection from "../reservations/components/subcompdash/CarDocumentsSection";
import MaintenCar from "../reservations/components/subcompdash/MaintenCar";
import OwnstatCar from "../reservations/components/subcompdash/OwnstatCar";
import ResCustomer from "../reservations/components/subcompdash/ResCustomer";
import TopBack from "../reservations/components/subcompdash/Topback";
import DisabledToggle from "./components/DisabledToggle";
import FeaturedToggle from "./components/FeaturedToggle";
import CarChangeLogTab from "./components/cardetails/CarChangeLogTab";
import CarDetailEditForm from "./components/cardetails/CarDetailEditForm";
import CarDetailFieldTable from "./components/cardetails/CarDetailFieldTable";
import CarDetailTabs from "./components/cardetails/CarDetailTabs";
import CarImagesGallery from "./components/cardetails/CarImagesGallery";
import displayValue from "../../utilities/displayValue";
import generateCarName from "./utils/generateCarName";
import { formatBlockDates, getBlockReasonLabel, isBlockInEffectToday } from "./utils/carBlockStatus";
import { useToast } from "../../contexts/ToastContext";
import { useUser } from "../../contexts/UserContext/UserContext";
import uploadFile from "../../utilities/uploadFile";
import deleteFile from "../../utilities/deleteFile";
import { useCarContext } from "../../contexts/CarContext";
import useCarPricingMode from "./hooks/useCarPricingMode";
import {
    buildRatePayload,
    createDefaultRate,
    getRateDisplayRows,
    isRateStepValid,
    normalizeRateValue,
} from "./components/addCar/RateFields";

/** Car + list payloads use Mongo `location`/`id` as string, or `{ _id, id }` when populated. */
function locationRefToId(loc) {
    if (loc == null || loc === "") return "";
    if (typeof loc === "string") return loc === "undefined" ? "" : loc;
    if (typeof loc === "object") return String(loc._id ?? loc.id ?? "");
    return "";
}

export default function Cardetails({ match }) {
    const { setCars } = useCarContext();
    const { t } = useTranslation("cars");
    const { t: tTopBar } = useTranslation("topBar");
    const [selectedLocationId, setSelectedLocationId] = useState("");
    // selectedLocationName removed — derived from allLocationOptions in render
    const [selectedColor, setSelectedColor] = useState("");
    const [editedImages, setEditedImages] = useState(null);
    const [car, setCar] = useState({
        id: "",
        carName: "",
        carNumber: "",
        VIN: "",
        bodyType: "",
        category: "",
        categoryName: "",
        location: "",
        serialNumber: "",
        config: {
            make: "",
            model: "",
            year: "",
            color: "",
            bags: "",
            seats: "",
            carSize: "",
            fuelType: "",
            transmission: "",
        },
        images: {
            image1: "",
            image2: "",
            image3: "",
            image4: "",
        },
        reservations: [],
        pastReservations: [],
        inspection: {
            last: "",
            exp: "",
            doc: "",
        },
        insurance: {
            company: "",
            policyNo: "",
            coverage: "",
            exp: "",
            doc: "",
        },
        maintanence: {
            vendor: "",
            price: "",
            odometer: "",
            comment: "",
            date: "",
        },
        registration: {
            state: "",
            exp: "",
            doc: "",
        },
        status: "Locked",
        isFeatured: false,
        isDisabled: false,
    });

    const [car1, setCar1] = useState({
        id: "",
        image: "",
        vehicle_name: "",
        vehicle_make: "",
        vehicle_license_plate: "",
    });

    const [initialLoad, setInitialLoad] = useState(true);
    const [form] = Form.useForm();

    // State declarations
    const [carEdit, setCarEdit] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [carCategories, setCarCategories] = useState([]);
    const [activeTabParam, setActive] = useUrlParamState("tab", 1, [1, 2, 3, 4, 5]);
    const active = Number(activeTabParam);
    const handleCarBlocksChange = useCallback((carBlocks) => {
        setCar((prevCar) => ({ ...prevCar, carBlocks }));
    }, []);
    const [allLocationOptions, setAllLocationOptions] = useState([]);
    const [, setAllLocations] = useState([]); // Store full location objects
    const [isInsuranceWarningModalOpen, setIsInsuranceWarningModalOpen] = useState(false);
    const [pendingLocationChange, setPendingLocationChange] = useState(null);
    const [insuranceWarningLoading, setInsuranceWarningLoading] = useState(false);
    const [showVinChangeWarning, setShowVinChangeWarning] = useState(false);
    const [pendingFormValues, setPendingFormValues] = useState(null);
    const toast = useToast();
    const { myCompanyData, fetchCompanyData } = useUser();
    const [isInsuranceEnabled, setIsInsuranceEnabled] = useState(false);
    const [isAbiInsuranceEnabled, setIsAbiInsuranceEnabled] = useState(false);
    const isCarPricingMode = useCarPricingMode();
    const [carRate, setCarRate] = useState(createDefaultRate);
    const [calenderDay, setCalenderDay] = useState(false);
    const history = useHistory();
    const location = useLocation();
    const [editOnLoad, setEditOnLoad] = useState(false);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const shouldEdit = location.state?.edit === true || searchParams.get("edit") === "true";
        setEditOnLoad(shouldEdit);
        if (!shouldEdit) {
            setCarEdit(false);
        }
    }, [location.pathname, location.search, location.state]);

    useEffect(() => {
        if (!initialLoad && editOnLoad && car?.VIN) {
            form.setFieldsValue(car);
            setCarEdit(true);
            setEditOnLoad(false);
        }
    }, [initialLoad, editOnLoad, car, form]);

    // Function to check if insurance providers are enabled for the company
    useEffect(() => {
        const insuranceCheck = () => {
            if (!myCompanyData?.insurance) {
                setIsInsuranceEnabled(false);
                setIsAbiInsuranceEnabled(false);
                return;
            }

            const teqmobilityInsurance = myCompanyData.insurance.find(
                (insurance) => insurance.provider === "teqmobility",
            );
            // INSHUR-DISABLED: kept false so the car page never shows the INSHUR fleet section
            // (status, Add/Remove buttons and their modals), even for companies that connected it.
            // const abiInsurance = myCompanyData.insurance.find((insurance) => insurance.provider === "abi");

            setIsInsuranceEnabled(teqmobilityInsurance?.enabled === true);
            // setIsAbiInsuranceEnabled(abiInsurance?.enabled === true);
            setIsAbiInsuranceEnabled(false);
        };

        // Only check if we have company data
        if (myCompanyData) {
            insuranceCheck();
        }
    }, [myCompanyData]);

    // Also add a separate useEffect to fetch company data on mount
    useEffect(() => {
        fetchCompanyData();
    }, [fetchCompanyData]);

    // Function declarations
    const fetchData = useCallback(async (serialNumber) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_SOFTCAR_VEHICLE_POSITION}?SerialNumber=${serialNumber}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            );
            return response.json();
        } catch (_err) {
            // Log error details for debugging
            return { error: true };
        }
    }, []);

    const reverseGeocode = useCallback(async (lat, lng) => {
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
            );
            const data = await response.json();
            if (data.results && data.results.length > 0) {
                // Address data is available for future use if needed
                // const addressResult = data.results[0].formatted_address;
            }
        } catch (_err) {
            // Error reverse geocoding
            toast.error({
                message: t("error"),
                description: t("carDetail.errorReverseGeocoding"),
                duration: 5,
            });
        }
    }, [toast, t]);

    const fetchLocations = useCallback(async () => {
        try {
            const response = await apiClient.get("/api/location/list?limit=200");
            if (response.data.success) {
                const locations = response.data.data;
                setAllLocations(locations); // Store full location objects
                setAllLocationOptions(
                    locations.map((loc) => ({
                        value: String(loc.id ?? loc._id ?? ""),
                        label: (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                {loc.isInsuranceEnabled ? (
                                    <BsShieldFillCheck
                                        style={{ color: "#22c55e", fontSize: "0.9rem" }}
                                        title="Insurance enabled"
                                    />
                                ) : (
                                    <BsShieldFillExclamation
                                        style={{ color: "#f59e0b", fontSize: "0.9rem" }}
                                        title="Insurance not enabled"
                                    />
                                )}
                                <span>{`${loc.city}, ${loc.state}`}</span>
                            </div>
                        ),
                        searchLabel: `${loc.city}, ${loc.state}`,
                        isInsuranceEnabled: loc.isInsuranceEnabled,
                    })),
                );
            }
        } catch (_err) {
            // Error fetching locations
            toast.error({
                message: t("error"),
                description: t("carDetail.errorFetchingLocations"),
                duration: 5,
            });
        }
    }, [toast, t]);

    const fetchCarCategories = useCallback(async () => {
        try {
            const response = await apiClient.get("/api/car-category-v2/list?limit=200");
            if (response.data.success) {
                setCarCategories(response.data.data);
                return response.data.data;
            }
            return [];
        } catch (_fetchError) {
            // Error fetching car categories
            return [];
        }
    }, []);

    const fetchCar = useCallback(
        async (vinToFetch = null) => {
            try {
                setInitialLoad(true);
                setCarEdit(false);
                // Use the provided VIN or fall back to match.params.id
                const targetVin = vinToFetch || match.params.id;
                const response = await apiClient.get(`/api/car/${targetVin}`);
                const carInfo = response.data.data;
                if (!carCategories?.length) {
                    await fetchCarCategories();
                }

                const normalizedCarInfo = {
                    ...carInfo,
                    isFeatured: carInfo.isFeatured ?? false,
                    isDisabled: carInfo.isDisabled ?? false,
                    category: typeof carInfo.category === "object" && carInfo.category !== null
                        ? String(carInfo.category._id ?? "")
                        : (carInfo.category ?? ""),
                };
                setCar(normalizedCarInfo);
                const carLocId =
                    locationRefToId(carInfo.location) || locationRefToId(carInfo.loc?.[0]) || "";
                setSelectedLocationId(carLocId);
                setSelectedColor(carInfo.config.color);
                setInitialLoad(false);
                const position = await fetchData(carInfo.serialNumber);
                if (!position.error) {
                    reverseGeocode(position.latitude, position.longitude);
                }
            } catch (_err) {
                setInitialLoad(false);
                // Error fetching car data
            }
        },
        [carCategories, match.params.id, fetchCarCategories, fetchData, reverseGeocode],
    );

    const onChangeSelectedLocation = (value) => {
        // With labelInValue, value is { value: string, label: ReactNode }
        const newId = value?.value ?? value;
        const selectedOption = allLocationOptions.find((loc) => loc.value === newId);
        const isCarInsuranceLinked = car?.insuranceVehicleInfo?.active;

        // If location doesn't have insurance enabled and car is linked to insurance, show warning
        if (!selectedOption?.isInsuranceEnabled && isCarInsuranceLinked) {
            setPendingLocationChange({
                value: newId,
                label: value.label,
                searchLabel: selectedOption?.searchLabel || "",
            });
            setIsInsuranceWarningModalOpen(true);
        } else {
            setSelectedLocationId(newId);
        }
    };

    const onChangeSelectedColor = (value) => {
        const colorValue = typeof value === "object" && value.value ? value.value : value;
        setSelectedColor(colorValue);
    };

    const handleInsuranceWarningConfirm = async () => {
        if (!pendingLocationChange) return;

        try {
            setInsuranceWarningLoading(true);

            // Update location immediately
            setSelectedLocationId(pendingLocationChange.value);

            // If car has insurance linked, we need to unlink it when form is submitted
            // This will be handled in handleFormSubmit

            setIsInsuranceWarningModalOpen(false);
            setPendingLocationChange(null);
        } catch (__error) {
            toast.error({
                message: t("error"),
                description: t("carDetail.insuranceWarningHandleFailed"),
            });
        } finally {
            setInsuranceWarningLoading(false);
        }
    };

    const handleInsuranceWarningCancel = () => {
        setIsInsuranceWarningModalOpen(false);
        setPendingLocationChange(null);
    };

    const processFormSubmit = async (values) => {
        setLoading(true);
        setError("");

        try {
            if (isCarPricingMode && !isRateStepValid(carRate)) {
                toast.error({
                    message: t("error"),
                    description: t("rates.required"),
                    duration: 4,
                });
                setLoading(false);
                return;
            }

            // Create a copy of values to modify
            const submissionData = { ...values };

            const imageKeys = ["image1", "image2", "image3", "image4"];
            const imagesToUpload = editedImages || submissionData.images || {};
            submissionData.images = submissionData.images || {};

            const uploadedEntries = await Promise.all(
                imageKeys.map(async (key) => {
                    const newValue = imagesToUpload[key];
                    const oldValue = car.images?.[key];

                    if (newValue instanceof File || (newValue && typeof newValue !== "string")) {
                        const imageUrl = await uploadFile(newValue);
                        if (imageUrl) {
                            if (oldValue && typeof oldValue === "string") {
                                await deleteFile(oldValue);
                            }
                            return [key, imageUrl];
                        }
                        return [key, oldValue || ""];
                    }

                    if (newValue === "" && oldValue) {
                        if (typeof oldValue === "string") {
                            await deleteFile(oldValue);
                        }
                        return [key, ""];
                    }

                    if (typeof newValue === "string") {
                        return [key, newValue];
                    }

                    return [key, submissionData.images[key] || oldValue || ""];
                }),
            );

            submissionData.images = Object.fromEntries(uploadedEntries);

            if (!submissionData.images.image1) {
                submissionData.images.image1 =
                    submissionData.images.image2 || submissionData.images.image3 || submissionData.images.image4 || "";
            }

            submissionData.carName = generateCarName(submissionData.config);
            submissionData.isFeatured = Boolean(submissionData.isFeatured);

            const effectiveLocationId = locationRefToId(selectedLocationId) || locationRefToId(car.location);
            if (!effectiveLocationId) {
                toast.error({
                    message: t("error"),
                    description: t("carDetail.selectLocation") || "Please select a location for this vehicle.",
                    duration: 4,
                });
                setLoading(false);
                return;
            }

            submissionData.location = effectiveLocationId;
            delete submissionData.baseLocation;
            delete submissionData.baseLocationId;
            if (!submissionData.config) submissionData.config = {};
            submissionData.config.color = selectedColor;

            // Check if car needs to be unlinked from insurance
            const isVinChanged = submissionData.VIN !== car.VIN;
            const selectedLocation = allLocationOptions.find((loc) => loc.value === effectiveLocationId);
            const isCarInsuranceLinked = car?.insuranceVehicleInfo?.active;
            const locationHasNoInsurance = !selectedLocation?.isInsuranceEnabled;

            // Unlink from insurance if VIN is changing or location doesn't support insurance
            const needsUnlinking = isCarInsuranceLinked && (isVinChanged || locationHasNoInsurance);

            if (needsUnlinking) {
                try {
                    await apiClient.delete(`/api/insurance/remove-vehicle-from-teqmobility/${car.id}`);
                } catch (_unlinkError) {
                    // If unlinking fails for VIN change, don't proceed
                    if (isVinChanged) {
                        toast.error({
                            message: t("error"),
                            description: t("carDetail.failedToUnlinkBeforeVinChange"),
                            duration: 5,
                        });
                        setLoading(false);
                        return;
                    }
                    // For location changes, log warning but continue
                    // Use toast instead of console
                    toast.warning({
                        message: t("warning"),
                        description: t("carDetail.errorUnlinkingInsurance"),
                        duration: 3,
                    });
                }
            }

            // Convert string values to numbers for numeric fields
            if (submissionData.config.year) {
                submissionData.config.year = Number(submissionData.config.year);
            }
            if (submissionData.config.bags) {
                submissionData.config.bags = Number(submissionData.config.bags);
            }
            if (submissionData.config.seats) {
                submissionData.config.seats = Number(submissionData.config.seats);
            }

            const { config, ...rest } = submissionData;

            const category = carCategories.find((cat) => String(cat._id) === submissionData.category);

            const prepareData = {
                ...car,
                config: {
                    ...car.config,
                    ...config,
                },
                ...rest,
                categoryName: category ? category?.categoryName : car.category?.categoryName ?? car.categoryName,
                bodyType: category ? category?.categoryName : car.category?.categoryName ?? car.bodyType,
                category: category ? String(category._id) : submissionData.category,
                ...(category && {
                    categoryId: category.categoryId ?? String(category._id),
                }),
            };

            prepareData.location = effectiveLocationId;
            delete prepareData.baseLocation;
            delete prepareData.baseLocationId;

            // Door lock status only — never send category/vehicle "active" values
            prepareData.status =
                car.status === "Unlocked" || car.status === "Locked" ? car.status : "Locked";

            if (isCarPricingMode) {
                prepareData.rate = buildRatePayload(carRate);
                prepareData.calenderDay = calenderDay;
            }

            if (needsUnlinking) {
                prepareData.insuranceVehicleInfo = null;
            }

            // Update the car data
            const response = await apiClient.patch(`/api/car/${car?.id}`, prepareData);

            if (response.data.success) {
                setCar(response.data.data);

                // update car list in car context
                setCars((prevCars) => prevCars.map((c) => (c.id === car.id ? response.data.data : c)));

                // Update insurance state if car was unlinked
                if (needsUnlinking) {
                    setCar((prevCar) => ({
                        ...prevCar,
                        insuranceVehicleInfo: { active: false },
                    }));

                    if (isVinChanged) {
                        toast.success({
                            message: t("success"),
                            description: `${t("carDetail.updateSuccessful")} ${t("carDetail.vehicleUnlinkedDueToVinChange")}`,
                            duration: 5,
                        });
                    } else {
                        toast.success({
                            message: t("success"),
                            description: `${t("carDetail.updateSuccessful")} ${t("carDetail.vehicleUnlinkedDueToLocationChange")}`,
                            duration: 5,
                        });
                    }
                } else {
                    // Normal success message
                    toast.success({
                        message: t("success"),
                        description: t("carDetail.updateSuccessful"),
                        duration: 2,
                    });
                }

                setSuccess(t("carDetail.updateSuccessful"));
                setCarEdit(false);
                setEditedImages(null);

                if (isVinChanged) {
                    history.push(`/cardetails/${response.data.data.VIN}`);
                } else {
                    await fetchCar(response.data.data.VIN);
                }

                setLoading(false);
            }
        } catch (submitError) {
            if (submitError.response?.data?.message === "Mongoose Duplicate Error") {
                if (submitError.response?.data?.errorSources[0]?.path === "VIN") {
                    setError(t("duplicateVINError"));
                }
                if (submitError.response?.data?.errorSources[0]?.path === "carNumber") {
                    setError(t("duplicatePlateError"));
                }
                if (submitError.response?.data?.errorSources[0]?.path === "ezPassTagNumber") {
                    setError(t("duplicateEzTagError"));
                }
            } else {
                // console.error(submitError);
                setError(submitError?.response?.data?.message || t("carDetail.updateError"));
            }
            setLoading(false);
        }
    };

    const handleFormSubmit = async (values) => {
        if (success === "") {
            // Check if VIN is being changed and car is linked to insurance
            const isVinChanged = values.VIN !== car.VIN;
            const isCarInsuranceLinked = car?.insuranceVehicleInfo?.active;

            if (isVinChanged && isCarInsuranceLinked) {
                // Show VIN change warning modal
                setPendingFormValues(values);
                setShowVinChangeWarning(true);
                return;
            }

            // Proceed with normal form submission
            await processFormSubmit(values);
        } else {
            setError(t("carDetail.waitABit"));
        }
    };

    const handleVinChangeConfirm = async () => {
        setShowVinChangeWarning(false);
        await processFormSubmit(pendingFormValues);
        setPendingFormValues(null);
    };

    const handleVinChangeCancel = () => {
        setShowVinChangeWarning(false);
        setPendingFormValues(null);
    };

    useEffect(() => {
        if (process.env.REACT_APP_APPLICATION_TYPE === "dronemobile" && car1.id === "") {
            fetch("https://softcar-chrentacar-live-64df6dffd12e.herokuapp.com/dronemobile")
                .then((res) => res.json())
                .then((res) => {
                    const matchingCar = res.find((element) => element.vin === match.params.id);
                    if (matchingCar) {
                        setCar1(matchingCar);
                    }
                })
                .catch(() => {
                    // Error fetching dronemobile data
                    toast.error({
                        message: t("error"),
                        description: t("carDetail.errorFetchingDronemobileData"),
                        duration: 7,
                    });
                });
        }

        fetchCar();
    }, [match.params.id, car1.id, fetchCar, toast, t]);

    useEffect(() => {
        fetchLocations();
        fetchCarCategories();
    }, [fetchLocations, fetchCarCategories]);

    useEffect(() => {
        setTimeout(() => setSuccess(""), 2000);
    }, [success]);
    useEffect(() => {
        setTimeout(() => setError(""), 2000);
    }, [error]);

    useEffect(() => {
        setCarRate(car?.rate ? normalizeRateValue(car.rate) : createDefaultRate());
        setCalenderDay(car?.calenderDay ?? false);
    }, [car?.id, car?.rate, car?.calenderDay]);

    const resetCarPricingFromCar = useCallback(() => {
        setCarRate(car?.rate ? normalizeRateValue(car.rate) : createDefaultRate());
        setCalenderDay(car?.calenderDay ?? false);
    }, [car?.rate, car?.calenderDay]);

    const na = t("notAvailable");
    const watchedConfig = Form.useWatch("config", form);
    const watchedCarNumber = Form.useWatch("carNumber", form);
    const generatedCarName =
        generateCarName(watchedConfig || car?.config) || car?.carName || "";

    const colorOptions = colornames.map((color) => ({
        value: color.name,
        label: (
            <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: color.hex }} />
                <span>{color.name}</span>
            </div>
        ),
    }));
    const carColor = colornames.find((color) => color.name === car?.config?.color);

    const CAR_DETAIL_TABS = [
        { id: 1, label: t("carDetail.reservationHistory") },
        { id: 2, label: t("maintenance") },
        { id: 3, label: t("carDetail.changeLog") },
        { id: 4, label: t("carDetail.ownedLeased") },
        { id: 5, label: t("carDetail.blockSchedule") },
    ];

    const locationDisplayText = (() => {
        const locOpt = allLocationOptions.find((loc) => loc.value === selectedLocationId);
        return displayValue(locOpt?.searchLabel || car?.baseLocation, na);
    })();

    const detailRows = [
        { label: t("category"), value: car?.category?.categoryName || car?.categoryName },
        {
            label: t("location"),
            render: () => {
                const locOpt = allLocationOptions.find((loc) => loc.value === selectedLocationId);
                return (
                    <div className="flex items-center gap-2">
                        {locOpt?.isInsuranceEnabled ? (
                            <BsShieldFillCheck className="text-sm text-green-500" title="Insurance enabled" />
                        ) : (
                            <BsShieldFillExclamation className="text-sm text-amber-500" title="Insurance not enabled" />
                        )}
                        <span>{locationDisplayText}</span>
                    </div>
                );
            },
        },
        { label: t("carDetail.movetraxKey"), value: car?.serialNumber },
        { label: t("vinNumberShort"), value: car?.VIN },
        { label: t("carDetail.make"), value: car?.config?.make },
        { label: t("model"), value: car?.config?.model },
        { label: t("year"), value: car?.config?.year },
        {
            label: t("color"),
            render: () => (
                <div className="flex items-center gap-2">
                    {carColor?.hex ? (
                        <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: carColor.hex }} />
                    ) : null}
                    <span>{displayValue(carColor?.name, na)}</span>
                </div>
            ),
        },
        { label: t("bags"), value: car?.config?.bags },
        { label: t("seats"), value: car?.config?.seats },
        ...(isCarPricingMode
            ? getRateDisplayRows(car?.rate, t).map((row) => ({ label: row.label, value: row.value }))
            : []),
        { label: t("carDetail.carSizeClass"), value: car?.config?.carSize },
        { label: t("fuelType"), value: car?.config?.fuelType },
        { label: t("carDetail.transmission"), value: car?.config?.transmission },
        { label: t("ezPassTagNumber"), value: car?.ezPassTagNumber },
        {
            label: t("worthValue"),
            value: car?.worthValue ? `$${car.worthValue.toLocaleString()}` : null,
        },
    ];

    const blocksInEffectToday = (car?.carBlocks || []).filter(isBlockInEffectToday);

    const renderActiveTab = () => {
        if (active === 1) {
            return (
                <ResCustomer
                    reservations={car.reservations}
                    pastReservations={car.pastReservations}
                    id={car.id}
                    loading={initialLoad}
                />
            );
        }
        if (active === 2 && car.id) {
            return <MaintenCar data={car?.VIN} id={car.id} status={car?.maintanence?.status || false} />;
        }
        if (active === 3) {
            return <CarChangeLogTab />;
        }
        if (active === 4) {
            return <OwnstatCar data={car} loading={initialLoad} />;
        }
        if (active === 5 && car.id) {
            return <BlockCar id={car.id} onBlocksChange={handleCarBlocksChange} />;
        }
        return null;
    };

    const handleDeleteCar = () => {
        Modal.confirm({
            title: t("confirmDelete"),
            okText: t("delete"),
            cancelText: t("rates.cancel"),
            centered: true,
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    const res = await apiClient.delete(`/api/car/${car.id}`);

                    if (res.data.success) {
                        window.location.href = "/cars";
                        return;
                    }

                    throw new Error("Delete failed");
                } catch (deleteError) {
                    setError(deleteError);
                    throw deleteError;
                }
            },
        });
    };

    const handleCancelCarEdit = () => {
        form.setFieldsValue(car);
        resetCarPricingFromCar();
        setEditedImages(null);
        setCarEdit(false);
    };

    const showDeleteButton =
        (process.env.REACT_APP_APPLICATION_TYPE === "dronemobile" && car1.id) ||
        process.env.REACT_APP_APPLICATION_TYPE === "moovetrax" ||
        (process.env.REACT_APP_APPLICATION_TYPE === "demoMoovetrax" && car.serialNumber);

    return (
        <>
            <div className="flex min-w-0 flex-col gap-3">
            <Spin spinning={loading} tip={t("carDetail.savingChanges")}>
            <div className="flex min-w-0 flex-col gap-3">
                <div className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-4">
                    <div className="flex min-w-0 flex-wrap items-center gap-3">
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined className="text-base text-[#2F2F46]" />}
                            onClick={() => history.goBack()}
                            aria-label={tTopBar("goBack")}
                            className="!h-9 !w-9 shrink-0 !rounded-lg !p-0 hover:!bg-[#F2F2FA] sm:hidden"
                        />
                        <span className="hidden sm:inline-flex">
                            <TopBack />
                        </span>
                        <h1 className="fontbold text-[1.3rem]">{t("carDetail.carDetails")}</h1>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        {error && <div className="text-red-600">{error}</div>}
                        {process.env.REACT_APP_APPLICATION_TYPE === "dronemobile" && car1.id === "" && (
                            <div className="text-[1.4rem]">{t("loading")}</div>
                        )}
                        {success && <div className="text-[#95EF1F]">{success}</div>}
                        {!initialLoad && car?.id ? (
                            <DisabledToggle
                                car={car}
                                onChange={(updated) => {
                                    setCar((prevCar) => ({ ...prevCar, isDisabled: updated.isDisabled }));
                                    setCars((prevCars) =>
                                        prevCars.map((c) =>
                                            c.id === updated.id ? { ...c, isDisabled: updated.isDisabled } : c,
                                        ),
                                    );
                                }}
                            />
                        ) : null}
                        {showDeleteButton ? (
                            <Button
                                danger
                                shape="round"
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={handleDeleteCar}>
                                {t("delete")}
                            </Button>
                        ) : null}
                    </div>
                </div>
                {!initialLoad && car?.isDisabled ? (
                    <Alert
                        type="error"
                        showIcon
                        className="!rounded-xl"
                        message={t("carDisabledWarningTitle")}
                        description={t("carDisabledWarningDescription")}
                    />
                ) : null}
                {!initialLoad && blocksInEffectToday.length > 0 ? (
                    <Alert
                        type="warning"
                        showIcon
                        className="!rounded-xl"
                        message={t("carDetail.carBlockedWarningTitle")}
                        description={blocksInEffectToday
                            .map((block) => `${getBlockReasonLabel(block.reason, t)} · ${formatBlockDates(block)}`)
                            .join(" | ")}
                        action={
                            active !== 5 ? (
                                <Button size="small" onClick={() => setActive(5)}>
                                    {t("carDetail.viewBlocks")}
                                </Button>
                            ) : null
                        }
                    />
                ) : null}
                <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(360px,420px)_minmax(0,1fr)]">
                    <div className="flex min-w-0 flex-col rounded-2xl border border-[#f0f0f0] bg-white">
                        <div className="flex shrink-0 flex-wrap items-start justify-between gap-3 border-b border-[#f0f0f0] px-4 py-3">
                            <div className="min-w-0 flex-1">
                                {initialLoad ? (
                                    <Skeleton active paragraph={{ rows: 2 }} />
                                ) : carEdit ? (
                                    <>
                                        <h2 className="fontbold text-lg text-[#121212]">
                                            {displayValue(generatedCarName, na)}
                                        </h2>
                                        <p className="mt-1 text-sm text-gray-600 font-semibold">
                                            {t("plateNumber")}: {displayValue(watchedCarNumber ?? car?.carNumber, na)}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="fontbold flex flex-wrap items-center gap-2 text-lg text-[#121212]">
                                            {displayValue(
                                                process.env.REACT_APP_APPLICATION_TYPE === "dronemobile"
                                                    ? `${car1.vehicle_name || ""} ${car1.vehicle_make || ""}`.trim()
                                                    : generateCarName(car?.config) || car.carName,
                                                na,
                                            )}
                                            {car?.isFeatured && (
                                                <Tag color="#5d5dff" className="rounded border-0">
                                                    {t("carDetail.featured")}
                                                </Tag>
                                            )}
                                            {car?.isDisabled && (
                                                <Tag color="error" className="rounded">
                                                    {t("disabledTag")}
                                                </Tag>
                                            )}
                                        </h2>
                                        <p className="mt-1 text-sm text-gray-600 font-semibold">
                                            {t("plateNumber")}:{" "}
                                            {displayValue(
                                                process.env.REACT_APP_APPLICATION_TYPE === "dronemobile"
                                                    ? car1.vehicle_license_plate
                                                    : car.carNumber,
                                                na,
                                            )}
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="flex shrink-0 items-center">
                                {carEdit && car ? (
                                    <Space size={8}>
                                        <Button
                                            type="primary"
                                            shape="round"
                                            size="small"
                                            loading={loading}
                                            onClick={() => form.submit()}>
                                            {t("rates.save")}
                                        </Button>
                                        <Button
                                            shape="round"
                                            size="small"
                                            disabled={loading}
                                            onClick={handleCancelCarEdit}>
                                            {t("rates.cancel")}
                                        </Button>
                                    </Space>
                                ) : (
                                    <Space size={8}>
                                        {!initialLoad && car?.id && (
                                            <FeaturedToggle
                                                car={car}
                                                withLabel
                                                onChange={(updated) => {
                                                    setCar((prevCar) => ({ ...prevCar, isFeatured: updated.isFeatured }));
                                                    setCars((prevCars) =>
                                                        prevCars.map((c) =>
                                                            c.id === updated.id
                                                                ? { ...c, isFeatured: updated.isFeatured }
                                                                : c,
                                                        ),
                                                    );
                                                }}
                                            />
                                        )}
                                        <Button
                                            type="primary"
                                            shape="round"
                                            size="small"
                                            ghost
                                            disabled={initialLoad}
                                            onClick={() => {
                                                if (!initialLoad) setCarEdit(true);
                                            }}>
                                            {t("edit")}
                                        </Button>
                                    </Space>
                                )}
                            </div>
                        </div>

                        <CarImagesGallery
                            images={car?.images}
                            isEdit={carEdit}
                            loading={initialLoad}
                            fallbackImage={
                                process.env.REACT_APP_APPLICATION_TYPE === "dronemobile" ? car1.image : undefined
                            }
                            onChange={setEditedImages}
                        />

                        {!carEdit ? (
                            initialLoad ? (
                                <div className="m-4">
                                    <Skeleton active paragraph={{ rows: 10 }} />
                                </div>
                            ) : (
                                <CarDetailFieldTable
                                    title={t("carDetail.carDetails")}
                                    rows={detailRows}
                                    naFallback={na}
                                />
                            )
                        ) : (
                            car && (
                                <CarDetailEditForm
                                    form={form}
                                    car={car}
                                    carCategories={carCategories}
                                    isCarPricingMode={isCarPricingMode}
                                    allLocationOptions={allLocationOptions}
                                    selectedLocationId={selectedLocationId}
                                    onChangeSelectedLocation={onChangeSelectedLocation}
                                    selectedColor={selectedColor}
                                    colorOptions={colorOptions}
                                    onChangeSelectedColor={onChangeSelectedColor}
                                    carRate={carRate}
                                    setCarRate={setCarRate}
                                    onFinish={handleFormSubmit}
                                    t={t}
                                />
                            )
                        )}
                    </div>

                    <div className="flex min-w-0 flex-col gap-3">
                        <CarDocumentsSection
                            car={car}
                            isLoading={initialLoad}
                            currentLocation={allLocationOptions.find((loc) => loc.value === selectedLocationId)}
                            isInsuranceEnabled={isInsuranceEnabled}
                            isAbiInsuranceEnabled={isAbiInsuranceEnabled}
                        />

                        <CarDetailTabs tabs={CAR_DETAIL_TABS} active={active} onChange={setActive}>
                            {renderActiveTab()}
                        </CarDetailTabs>
                    </div>
                </div>
            </div>
            </Spin>
            </div>

            {/* Insurance Warning Modal */}
            <Modal
                title={t("carDetail.insuranceWarningTitle")}
                open={isInsuranceWarningModalOpen}
                onOk={handleInsuranceWarningConfirm}
                onCancel={handleInsuranceWarningCancel}
                okText={t("carDetail.confirmLocationChange")}
                cancelText={t("cancel")}
                okButtonProps={{
                    danger: true,
                    loading: insuranceWarningLoading,
                    shape: "round",
                }}
                cancelButtonProps={{ shape: "round" }}
                confirmLoading={insuranceWarningLoading}>
                <p>{t("carDetail.insuranceWarningMessage")}</p>
                <p>{t("carDetail.insuranceUnlinkWarning")}</p>
            </Modal>

            {/* VIN Change Warning Modal */}
            <Modal
                title={t("carDetail.vinChangeWarningTitle")}
                open={showVinChangeWarning}
                onOk={handleVinChangeConfirm}
                onCancel={handleVinChangeCancel}
                okText={t("carDetail.confirmVinChange")}
                cancelText={t("cancel")}
                okButtonProps={{
                    danger: true,
                    loading,
                    shape: "round",
                }}
                cancelButtonProps={{ shape: "round" }}
                confirmLoading={loading}>
                <p>{t("carDetail.vinChangeWarningMessage")}</p>
                <p>{t("carDetail.vinChangeInsuranceUnlinkWarning")}</p>
            </Modal>
        </>
    );
}

Cardetails.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            id: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
};
